import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useActiveTeam } from "@/hooks/use-team";
import { useTranslations } from "@/hooks/use-translations";
import { api, type ManualAssetTool, type MetaAdsAssets } from "@/lib/api";

/**
 * Manual Meta Ads parameters, one case per thing the agent may need.
 *
 * Three cases are entered as a bundle because Meta refuses the parts on their
 * own: an app id without its store url, a catalog without a product set, an
 * Instagram account without the page it belongs to. The rest stand alone.
 */

type CaseId = ManualAssetTool;

const CASE_ORDER: CaseId[] = [
	"app",
	"catalog",
	"instagram",
	"page",
	"pixel",
	"place_page_set",
];

// Which config key the main value field writes into
const VALUE_KEY: Record<CaseId, string> = {
	app: "application_id",
	catalog: "product_catalog_id",
	instagram: "account_id",
	page: "account_id",
	pixel: "id",
	place_page_set: "place_page_set_id",
};

export function MetaAdsParams() {
	const t = useTranslations("connectors");
	const queryClient = useQueryClient();
	const { activeTeam } = useActiveTeam();
	const teamId = activeTeam?.id ?? null;

	const [kind, setKind] = useState<CaseId>("app");
	const [name, setName] = useState("");
	const [value, setValue] = useState("");
	const [storeUrl, setStoreUrl] = useState("");
	const [platform, setPlatform] = useState<"ios" | "android">("ios");
	const [setName_, setSetName] = useState("");
	const [setId, setSetId] = useState("");
	const [parentPage, setParentPage] = useState("");
	const [newSets, setNewSets] = useState<Record<string, { id: string; name: string }>>({});

	const { data: assets, isLoading, error } = useQuery<MetaAdsAssets>({
		queryKey: ["metaAdsAssets", teamId],
		queryFn: () => api.getMetaAdsAssets(teamId),
		retry: false,
	});

	const LABELS: Record<CaseId, string> = {
		app: t("paramApp"),
		catalog: t("paramCatalog"),
		instagram: t("paramInstagram"),
		page: t("paramPage"),
		pixel: t("paramPixel"),
		place_page_set: t("paramPlacePageSet"),
	};

	const DESCRIPTIONS: Record<CaseId, string> = {
		app: t("paramAppDesc"),
		catalog: t("paramCatalogDesc"),
		instagram: t("paramInstagramDesc"),
		page: t("paramPageDesc"),
		pixel: t("paramPixelDesc"),
		place_page_set: t("paramPlacePageSetDesc"),
	};

	const NAME_LABELS: Record<CaseId, string> = {
		app: t("paramName"),
		catalog: t("paramCatalogName"),
		instagram: t("paramName"),
		page: t("paramName"),
		pixel: t("paramName"),
		place_page_set: t("paramName"),
	};

	const VALUE_LABELS: Record<CaseId, string> = {
		app: t("paramAppId"),
		catalog: t("paramCatalogId"),
		instagram: t("paramInstagramId"),
		page: t("paramPageId"),
		pixel: t("paramValue"),
		place_page_set: t("paramValue"),
	};

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: ["metaAdsAssets", teamId] });

	const resetForm = () => {
		setName("");
		setValue("");
		setStoreUrl("");
		setSetName("");
		setSetId("");
		setParentPage("");
	};

	const saveMutation = useMutation({
		mutationFn: () => {
			const config: Record<string, unknown> = {
				[VALUE_KEY[kind]]: value.trim(),
				name: name.trim(),
			};

			if (kind === "app") {
				config.object_store_url = storeUrl.trim();
				config.platform = platform;
			}

			if (kind === "catalog") {
				config.product_sets = [{ id: setId.trim(), name: setName_.trim() }];
			}

			if (kind === "instagram") {
				config.parent_account_id = parentPage;
			}

			return api.saveMetaAdsAsset(kind, config, teamId);
		},
		onSuccess: () => {
			toast.success(t("paramAdded"));
			resetForm();
			invalidate();
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : t("paramSaveError"));
		},
	});

	const deleteMutation = useMutation({
		mutationFn: ({ tool, rowId }: { tool: ManualAssetTool; rowId: string }) =>
			api.deleteMetaAdsAsset(tool, rowId),
		onSuccess: () => {
			toast.success(t("paramDeleted"));
			invalidate();
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : t("paramDeleteError"));
		},
	});

	// A product set has no row of its own: it lives inside its catalog's config,
	// so changing the list means writing the whole catalog back.
	const productSetsMutation = useMutation({
		mutationFn: ({
			catalogId,
			catalogName,
			sets,
		}: {
			catalogId: string;
			catalogName: string;
			sets: { id: string; name: string }[];
		}) =>
			api.saveMetaAdsAsset(
				"catalog",
				{ product_catalog_id: catalogId, name: catalogName, product_sets: sets },
				teamId,
			),
		onSuccess: () => invalidate(),
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : t("paramSaveError"));
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!canSubmit) return;
		saveMutation.mutate();
	};

	const handleAddSet = (
		catalogId: string,
		catalogName: string,
		current: { id: string; name: string }[],
	) => {
		const draft = newSets[catalogId];
		if (!draft?.id.trim() || !draft.name.trim()) return;

		productSetsMutation.mutate({
			catalogId,
			catalogName,
			sets: [...current, { id: draft.id.trim(), name: draft.name.trim() }],
		});
		setNewSets((prev) => ({ ...prev, [catalogId]: { id: "", name: "" } }));
	};

	if (error) {
		const noAccount = error.message === "No Meta ad account connected";
		return (
			<div className="rounded-xl border border-border-subtle p-4">
				<p className="text-sm text-text-tertiary">
					{noAccount ? t("connectAccountFirst") : t("paramsLoadError")}
				</p>
				{!noAccount && <p className="mt-1 text-xs text-text-tertiary">{error.message}</p>}
			</div>
		);
	}

	const pages = assets?.pages ?? [];

	// Bundled cases stay disabled until every part of the bundle is filled
	const canSubmit = Boolean(
		name.trim() &&
			value.trim() &&
			(kind !== "app" || storeUrl.trim()) &&
			(kind !== "catalog" || (setId.trim() && setName_.trim())) &&
			(kind !== "instagram" || parentPage),
	);

	const rowClass =
		"flex items-center justify-between gap-4 rounded-lg border border-border-subtle px-3 py-2.5";

	const deleteButton = (tool: ManualAssetTool, rowId: string) => (
		<Button
			type="button"
			variant="ghost"
			size="icon-sm"
			onClick={() => deleteMutation.mutate({ tool, rowId })}
			disabled={deleteMutation.isPending}
			className="text-text-tertiary hover:text-danger"
		>
			<Trash2 size={15} />
		</Button>
	);

	const listRow = (
		key: string,
		tool: ManualAssetTool,
		rowId: string,
		title: string,
		subtitle: string,
	) => (
		<div key={key} className={rowClass}>
			<div className="min-w-0">
				<div className="flex items-center gap-2">
					<span className="text-sm text-text-primary truncate">{title}</span>
					<span className="text-2xs uppercase tracking-wide text-text-tertiary">
						{LABELS[tool]}
					</span>
				</div>
				<p className="text-xs text-text-tertiary truncate">{subtitle}</p>
			</div>
			{deleteButton(tool, rowId)}
		</div>
	);

	const total =
		(assets?.apps.length ?? 0) +
		(assets?.catalogs.length ?? 0) +
		(assets?.placePageSets.length ?? 0) +
		(assets?.pixels.length ?? 0) +
		pages.length +
		(assets?.instagram.length ?? 0);

	return (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-text-primary">{t("metaAdsParams")}</h4>
				<p className="text-sm text-text-tertiary">{t("metaAdsParamsDescription")}</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-3">
				<div className="grid gap-3 sm:grid-cols-3">
					<label className="flex flex-col gap-1.5">
						<span className="text-xs text-text-tertiary">{t("paramType")}</span>
						<Select
							value={kind}
							onChange={(e) => {
								setKind(e.target.value as CaseId);
								resetForm();
							}}
						>
							{CASE_ORDER.map((key) => (
								<option key={key} value={key} className="bg-bg-surface">
									{LABELS[key]}
								</option>
							))}
						</Select>
					</label>

					<label className="flex flex-col gap-1.5">
						<span className="text-xs text-text-tertiary">{NAME_LABELS[kind]}</span>
						<Input
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("paramNamePlaceholder")}
						/>
					</label>

					<label className="flex flex-col gap-1.5">
						<span className="text-xs text-text-tertiary">{VALUE_LABELS[kind]}</span>
						<Input
							type="text"
							value={value}
							onChange={(e) => setValue(e.target.value)}
							placeholder={t("paramValuePlaceholder")}
						/>
					</label>
				</div>

				<p className="text-xs text-text-tertiary leading-relaxed">{DESCRIPTIONS[kind]}</p>

				{kind === "app" && (
					<div className="grid gap-3 sm:grid-cols-3">
						<label className="flex flex-col gap-1.5 sm:col-span-2">
							<span className="text-xs text-text-tertiary">{t("paramStoreUrl")}</span>
							<Input
								type="text"
								value={storeUrl}
								onChange={(e) => setStoreUrl(e.target.value)}
								placeholder={t("paramStoreUrlPlaceholder")}
							/>
						</label>
						<label className="flex flex-col gap-1.5">
							<span className="text-xs text-text-tertiary">{t("paramPlatform")}</span>
							<Select
								value={platform}
								onChange={(e) => setPlatform(e.target.value as "ios" | "android")}
							>
								<option value="ios" className="bg-bg-surface">
									{t("platformIos")}
								</option>
								<option value="android" className="bg-bg-surface">
									{t("platformAndroid")}
								</option>
							</Select>
						</label>
					</div>
				)}

				{kind === "catalog" && (
					<div className="grid gap-3 sm:grid-cols-2">
						<label className="flex flex-col gap-1.5">
							<span className="text-xs text-text-tertiary">{t("productSetName")}</span>
							<Input
								type="text"
								value={setName_}
								onChange={(e) => setSetName(e.target.value)}
								placeholder={t("productSetName")}
							/>
						</label>
						<label className="flex flex-col gap-1.5">
							<span className="text-xs text-text-tertiary">{t("productSetId")}</span>
							<Input
								type="text"
								value={setId}
								onChange={(e) => setSetId(e.target.value)}
								placeholder={t("productSetId")}
							/>
						</label>
					</div>
				)}

				{kind === "instagram" && (
					<label className="flex flex-col gap-1.5">
						<span className="text-xs text-text-tertiary">{t("paramParentPage")}</span>
						{pages.length === 0 ? (
							<p className="text-xs text-warning">{t("noPagesYet")}</p>
						) : (
							<Select
								value={parentPage}
								onChange={(e) => setParentPage(e.target.value)}
							>
								<option value="" className="bg-bg-surface">
									—
								</option>
								{pages.map((page) => (
									<option key={page.rowId} value={page.accountId} className="bg-bg-surface">
										{page.name}
									</option>
								))}
							</Select>
						)}
					</label>
				)}

				<Button type="submit" variant="secondary" disabled={!canSubmit || saveMutation.isPending}>
					{saveMutation.isPending && <Loader2 size={16} className="animate-spin" />}
					{t("addParam")}
				</Button>
			</form>

			<div className="space-y-2">
				{isLoading ? (
					<div className="flex items-center gap-2 text-sm text-text-tertiary">
						<Loader2 size={14} className="animate-spin" />
					</div>
				) : total === 0 ? (
					<p className="text-sm text-text-tertiary">{t("noParams")}</p>
				) : (
					<>
						{assets?.apps.map((row) =>
							listRow(
								row.rowId,
								"app",
								row.rowId,
								row.config.name,
								`${row.config.application_id} · ${row.config.platform} · ${row.config.object_store_url}`,
							),
						)}

						{assets?.catalogs.map((row) => {
							const sets = row.config.product_sets ?? [];
							const draft = newSets[row.config.product_catalog_id] ?? { id: "", name: "" };

							return (
								<div
									key={row.rowId}
									className="rounded-lg border border-border-subtle px-3 py-2.5 space-y-2.5"
								>
									<div className="flex items-center justify-between gap-4">
										<div className="min-w-0">
											<div className="flex items-center gap-2">
												<span className="text-sm text-text-primary truncate">{row.config.name}</span>
												<span className="text-2xs uppercase tracking-wide text-text-tertiary">
													{LABELS.catalog}
												</span>
											</div>
											<p className="text-xs text-text-tertiary truncate">
												{row.config.product_catalog_id}
											</p>
										</div>
										{deleteButton("catalog", row.rowId)}
									</div>

									<div className="pl-3 border-l border-border-subtle space-y-2">
										<span className="text-2xs uppercase tracking-wide text-text-tertiary">
											{t("productSets")}
										</span>

										{sets.length === 0 ? (
											<p className="text-xs text-text-tertiary">{t("noProductSets")}</p>
										) : (
											sets.map((set) => (
												<div key={set.id} className="flex items-center justify-between gap-3">
													<p className="text-xs text-text-tertiary truncate">
														{set.name} <span className="text-text-tertiary">· {set.id}</span>
													</p>
													<Button
														type="button"
														variant="ghost"
														size="icon-xs"
														disabled={productSetsMutation.isPending}
														onClick={() =>
															productSetsMutation.mutate({
																catalogId: row.config.product_catalog_id,
																catalogName: row.config.name,
																sets: sets.filter((s) => s.id !== set.id),
															})
														}
														className="text-text-tertiary hover:text-danger"
													>
														<Trash2 size={13} />
													</Button>
												</div>
											))
										)}

										<div className="flex flex-wrap items-center gap-2 pt-0.5">
											<Input
												type="text"
												value={draft.name}
												onChange={(e) =>
													setNewSets((prev) => ({
														...prev,
														[row.config.product_catalog_id]: { ...draft, name: e.target.value },
													}))
												}
												placeholder={t("productSetName")}
												className="h-8 w-40 px-2.5 text-xs"
											/>
											<Input
												type="text"
												value={draft.id}
												onChange={(e) =>
													setNewSets((prev) => ({
														...prev,
														[row.config.product_catalog_id]: { ...draft, id: e.target.value },
													}))
												}
												placeholder={t("productSetId")}
												className="h-8 w-40 px-2.5 text-xs"
											/>
											<Button
												type="button"
												variant="secondary"
												size="sm"
												disabled={
													!draft.id.trim() || !draft.name.trim() || productSetsMutation.isPending
												}
												onClick={() =>
													handleAddSet(row.config.product_catalog_id, row.config.name, sets)
												}
												className="gap-1"
											>
												<Plus size={13} />
												{t("addProductSet")}
											</Button>
										</div>
									</div>
								</div>
							);
						})}

						{assets?.instagram.map((row) =>
							listRow(
								row.rowId,
								"instagram",
								row.rowId,
								row.name,
								`${row.accountId}${row.parentAccountId ? ` · ${row.parentAccountId}` : ""}`,
							),
						)}

						{pages.map((row) =>
							listRow(row.rowId, "page", row.rowId, row.name, row.accountId),
						)}

						{assets?.pixels.map((row) =>
							listRow(
								row.rowId,
								"pixel",
								row.rowId,
								row.config.name ?? row.config.id,
								row.config.id,
							),
						)}

						{assets?.placePageSets.map((row) =>
							listRow(
								row.rowId,
								"place_page_set",
								row.rowId,
								row.config.name,
								row.config.place_page_set_id,
							),
						)}
					</>
				)}
			</div>
		</div>
	);
}
